import {Injectable} from '@angular/core';

const EXTERNAL_RECRUITMENT_ID_PARAMETER: string = 'external_recruitment_id';
const EXTERNAL_RECRUITMENT_ID_STORAGE_KEY: string = 'external_recruitment_id';
const MAX_EXTERNAL_RECRUITMENT_ID_LENGTH: number = 255;

/**
 * Preserves a panel provider's recruitment identifier for the lifetime of the
 * browser tab. It is captured from the landing URL and included when the user
 * is added to a community.
 */
@Injectable({
    providedIn: 'root'
})
export class RecruitmentTrackingService {
    private inMemoryExternalRecruitmentId: string | null = null;

    captureFromCurrentUrl(): void {
        const externalRecruitmentId: string | null = new URLSearchParams(window.location.search)
            .get(EXTERNAL_RECRUITMENT_ID_PARAMETER);

        if (externalRecruitmentId) {
            this.set(externalRecruitmentId);
        }
    }

    get(): string | null {
        if (this.inMemoryExternalRecruitmentId) {
            return this.inMemoryExternalRecruitmentId;
        }

        try {
            const externalRecruitmentId: string | null = sessionStorage.getItem(EXTERNAL_RECRUITMENT_ID_STORAGE_KEY);
            this.inMemoryExternalRecruitmentId = this.normalise(externalRecruitmentId);
        } catch {
            // sessionStorage can be unavailable (for example, when blocked by
            // browser privacy settings); retain the value for this app instance.
        }

        return this.inMemoryExternalRecruitmentId;
    }

    clear(): void {
        this.inMemoryExternalRecruitmentId = null;
        try {
            sessionStorage.removeItem(EXTERNAL_RECRUITMENT_ID_STORAGE_KEY);
        } catch {
            // Nothing further to clear when sessionStorage is unavailable.
        }
    }

    private set(externalRecruitmentId: string): void {
        const normalisedExternalRecruitmentId: string | null = this.normalise(externalRecruitmentId);
        if (!normalisedExternalRecruitmentId) {
            return;
        }

        this.inMemoryExternalRecruitmentId = normalisedExternalRecruitmentId;
        try {
            sessionStorage.setItem(EXTERNAL_RECRUITMENT_ID_STORAGE_KEY, normalisedExternalRecruitmentId);
        } catch {
            // The in-memory copy above is enough for the current app instance.
        }
    }

    private normalise(externalRecruitmentId: string | null): string | null {
        const value: string = externalRecruitmentId?.trim() ?? '';
        return value && value.length <= MAX_EXTERNAL_RECRUITMENT_ID_LENGTH ? value : null;
    }
}
